import { Code2, Bot, Workflow, Zap, CheckCircle2, ArrowRight, Sparkles, Clock, TrendingUp, Target, Cog, BarChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";

const ProcessAutomation = () => {
  const solutions = [
    {
      icon: Bot,
      title: "RPA (Robotic Process Automation)",
      description: "Robôs que executam tarefas repetitivas com precisão e velocidade.",
      color: "from-blue-500/20 to-blue-600/20",
      features: [
        "Automação de tarefas manuais",
        "Processamento de documentos",
        "Extração de dados",
        "Integração com sistemas legados"
      ]
    },
    {
      icon: Workflow,
      title: "Workflows Inteligentes",
      description: "Fluxos de trabalho automatizados que conectam pessoas e sistemas.",
      color: "from-purple-500/20 to-purple-600/20",
      features: [
        "Aprovações automáticas",
        "Notificações inteligentes",
        "Roteamento de tarefas",
        "Gestão de processos"
      ]
    },
    {
      icon: Zap,
      title: "Integração de Sistemas",
      description: "Conecte todos os seus sistemas para trabalhar em harmonia.",
      color: "from-orange-500/20 to-orange-600/20",
      features: [
        "APIs e webhooks",
        "Sincronização de dados",
        "ETL (Extract, Transform, Load)",
        "Middleware customizado"
      ]
    },
    {
      icon: Target,
      title: "Automação de Marketing",
      description: "Automatize campanhas e nutrição de leads com eficiência.",
      color: "from-green-500/20 to-green-600/20",
      features: [
        "Email marketing automatizado",
        "Segmentação de audiência",
        "Lead scoring",
        "Campanhas multicanal"
      ]
    },
    {
      icon: Cog,
      title: "Automação de Processos de Negócio",
      description: "Otimize processos complexos de ponta a ponta.",
      color: "from-indigo-500/20 to-indigo-600/20",
      features: [
        "BPM (Business Process Management)",
        "Modelagem de processos",
        "Monitoramento em tempo real",
        "Otimização contínua"
      ]
    },
    {
      icon: BarChart,
      title: "Analytics e Relatórios",
      description: "Dashboards e relatórios automáticos para tomada de decisão.",
      color: "from-pink-500/20 to-pink-600/20",
      features: [
        "Dashboards em tempo real",
        "Relatórios automatizados",
        "KPIs e métricas",
        "Business Intelligence"
      ]
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: "Economia de Tempo",
      value: "Até 80%",
      description: "Redução no tempo de execução de tarefas repetitivas"
    },
    {
      icon: TrendingUp,
      title: "Aumento de Produtividade",
      value: "3x mais",
      description: "Sua equipe pode focar em atividades estratégicas"
    },
    {
      icon: Target,
      title: "Redução de Erros",
      value: "99.9%",
      description: "Precisão na execução de processos automatizados"
    },
    {
      icon: Zap,
      title: "ROI Rápido",
      value: "6 meses",
      description: "Retorno sobre investimento em média"
    }
  ];

  const useCases = [
    {
      title: "Financeiro",
      items: [
        "Conciliação bancária",
        "Processamento de notas fiscais",
        "Geração de relatórios",
        "Controle de despesas"
      ]
    },
    {
      title: "RH",
      items: [
        "Onboarding de funcionários",
        "Processamento de folha",
        "Gestão de benefícios",
        "Controle de ponto"
      ]
    },
    {
      title: "Vendas",
      items: [
        "Qualificação de leads",
        "Follow-up automático",
        "Geração de propostas",
        "Atualização de CRM"
      ]
    },
    {
      title: "Atendimento",
      items: [
        "Chatbots inteligentes",
        "Triagem de tickets",
        "Respostas automáticas",
        "Escalação de casos"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-primary/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)] pointer-events-none" />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/30 backdrop-blur-xl sticky top-0 z-50 relative">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/">
            <img src="/logo.png" alt="Coddar Logo" className="h-11 w-auto" />
          </Link>
          <nav className="hidden md:flex gap-6">
            <Link to="/#servicos" className="text-muted-foreground hover:text-primary transition-colors">
              Serviços
            </Link>
            <Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">
              Blog
            </Link>
            <Link to="/seguranca-digital" className="text-muted-foreground hover:text-primary transition-colors">
              Segurança Digital
            </Link>
            <Link to="/contato" className="text-muted-foreground hover:text-primary transition-colors">
              Contato
            </Link>
          </nav>
          <Button asChild variant="outline" className="border-primary/50 text-primary hover:bg-primary/10 hover:border-primary">
            <a href="https://folio.app.br/area-cliente">Área do Cliente</a>
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 md:py-32 px-4 relative">
        <div className="container mx-auto text-center relative z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8">
            <ArrowRight className="h-4 w-4 rotate-180" />
            Voltar para Home
          </Link>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6 backdrop-blur-sm">
            <Sparkles className="h-4 w-4 text-primary animate-pulse" />
            <span className="text-sm font-medium text-primary">Automação de Processos</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 max-w-5xl mx-auto leading-tight">
            Automatize e{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-accent to-primary bg-[length:200%_auto] animate-[gradient_3s_linear_infinite]">
              potencialize
            </span>
            {" "}seu negócio
          </h1>

          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Elimine tarefas repetitivas, reduza custos e aumente a produtividade com automação inteligente
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild className="gap-2 group">
              <Link to="/contato">
                <span className="flex items-center gap-2">
                  Automatizar Agora <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contato">Ver Casos de Uso</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card
                  key={index}
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:scale-105 group text-center"
                >
                  <CardHeader>
                    <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform relative">
                      <Icon className="h-8 w-8 text-primary relative z-10" />
                      <div className="absolute inset-0 bg-primary/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="text-3xl font-bold text-primary mb-2">{benefit.value}</div>
                    <CardTitle className="text-lg text-foreground">{benefit.title}</CardTitle>
                    <CardDescription className="text-sm text-muted-foreground">
                      {benefit.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20 px-4 relative bg-card/30">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
              Soluções de Automação
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Automatize qualquer processo do seu negócio com nossas soluções especializadas
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <Card
                  key={index}
                  className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(99,159,255,0.2)] hover:scale-[1.02] group"
                >
                  <CardHeader>
                    <div className={`h-16 w-16 rounded-xl bg-gradient-to-br ${solution.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform relative`}>
                      <Icon className="h-8 w-8 text-primary relative z-10" />
                      <div className="absolute inset-0 bg-primary/30 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <CardTitle className="text-xl text-foreground">{solution.title}</CardTitle>
                    <CardDescription className="text-base text-muted-foreground">
                      {solution.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {solution.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Casos de Uso por Departamento
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Veja como a automação pode transformar cada área da sua empresa
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <Card
                key={index}
                className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group"
              >
                <CardHeader>
                  <CardTitle className="text-xl text-foreground">{useCase.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {useCase.items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative">
        <div className="container mx-auto relative z-10">
          <Card className="bg-gradient-to-br from-primary/20 via-card/50 to-accent/20 border-primary/30 backdrop-blur-xl overflow-hidden relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/10 to-primary/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
            <CardHeader className="text-center pb-6 relative z-10">
              <CardTitle className="text-3xl md:text-5xl mb-4 text-foreground">
                Pronto para automatizar?
              </CardTitle>
              <CardDescription className="text-foreground/80 text-lg max-w-2xl mx-auto">
                Agende uma consulta gratuita e descubra como podemos otimizar seus processos
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-center relative z-10">
              <Button size="lg" asChild className="gap-2 group/btn">
                <Link to="/contato">
                  <span className="flex items-center gap-2">
                    Agendar Consulta <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default ProcessAutomation;
